import { FeatureCalendar } from '@mdlbeasts/nofomo.ui.web';
import { useQueries, UseQueryResult } from '@tanstack/react-query';

import {
  CALENDAR_EVENTS_PER_MONTH,
  CALENDAR_MONTH_PADDING_DAYS,
} from 'constants/calendar.constants';
import { useLocale } from 'hooks/useLocale';
import { EventCardFragment } from 'types/api/dato-operations.types';

import { calendarMonthEventsQuery } from './queries';

/**
 * The window a month is fetched over: the month itself plus a day either side.
 * The bounds are UTC while an event is placed by its own timezone, so the pad
 * keeps an event at either edge from falling outside every window.
 */
const monthWindow = ({ year, month }: FeatureCalendar.CalendarMonth) => ({
  from: new Date(
    Date.UTC(year, month, 1 - CALENDAR_MONTH_PADDING_DAYS),
  ).toISOString(),
  to: new Date(
    Date.UTC(year, month + 1, 1 + CALENDAR_MONTH_PADDING_DAYS),
  ).toISOString(),
});

/**
 * Stable reference, so react-query only re-runs it when a query result changes.
 *
 * The windows overlap on purpose, and a run spanning months matches several of
 * them, so the same event arrives from more than one query. Without the dedupe it
 * would stack a second cover on the cell and a second row in the day sheet.
 */
const combine = (
  results: Array<UseQueryResult<{ events: Array<EventCardFragment> }>>,
) => {
  const byId = new Map<string, EventCardFragment>();

  for (const result of results) {
    for (const event of result.data?.events ?? []) byId.set(event.id, event);
  }

  return {
    events: [...byId.values()],
    isLoading: results.some((result) => result.isLoading),
  };
};

interface Props {
  months: Array<FeatureCalendar.CalendarMonth>;
}

// Every visible month, each its own cached query.
export const useCalendarMonthEvents = ({ months }: Props) => {
  const { locale } = useLocale();

  return useQueries({
    queries: months.map((month) => {
      const { from, to } = monthWindow(month);

      return calendarMonthEventsQuery({
        locale,
        filter: {
          unlistedEvent: { eq: false },
          // Overlap, so an event spanning a month boundary lands in both.
          startDatetime: { lt: to },
          endDatetime: { gte: from },
        },
        orderBy: 'startDatetime_ASC',
        first: CALENDAR_EVENTS_PER_MONTH,
        skip: 0,
      });
    }),
    combine,
  });
};
